import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  ReactNode,
} from "react";
import { feedbackService } from "@/services";
import {
  ClientFeedback,
  SubmitFeedbackPayload,
} from "@shared/types/FeedbackDto";
import { useAuthContext } from "./AuthProvider";

interface FeedbackDismissal {
  milestoneId?: string;
  projectId?: string;
  timestamp: number;
}

interface FeedbackContextType {
  feedback: ClientFeedback[];
  isLoading: boolean;
  error: string | null;
  isModalOpen: boolean;
  modalInitialType: "feedback" | "issue" | "suggestion";
  fetchFeedback: () => Promise<void>;
  submitFeedback: (payload: SubmitFeedbackPayload) => Promise<ClientFeedback>;
  updateFeedback: (
    feedbackId: string,
    payload: Partial<SubmitFeedbackPayload>,
  ) => Promise<void>;
  openFeedbackModal: (type?: "feedback" | "issue" | "suggestion") => void;
  closeFeedbackModal: () => void;
  isDismissed: (milestoneId?: string, projectId?: string) => boolean;
  dismissPrompt: (milestoneId?: string, projectId?: string) => void;
  hasRecentSubmission: () => boolean;
}

const FeedbackContext = createContext<FeedbackContextType | null>(null);

const STORAGE_KEY_DISMISSED = "feedback_dismissed";
const STORAGE_KEY_LAST_SUBMISSION = "feedback_last_submission";
const DISMISSAL_DURATION = 7 * 24 * 60 * 60 * 1000;
const SUBMISSION_COOLDOWN = 7 * 24 * 60 * 60 * 1000;

export function FeedbackProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuthContext();
  const [feedback, setFeedback] = useState<ClientFeedback[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalInitialType, setModalInitialType] = useState<
    "feedback" | "issue" | "suggestion"
  >("feedback");

  const fetchFeedback = useCallback(async () => {
    if (!isAuthenticated) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await feedbackService.getMyFeedback();
      setFeedback(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch feedback");
      console.error("Failed to fetch feedback:", err);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchFeedback();
    }
  }, [isAuthenticated, fetchFeedback]);

  const submitFeedback = async (
    payload: SubmitFeedbackPayload,
  ): Promise<ClientFeedback> => {
    const newFeedback = await feedbackService.submitFeedback(payload);
    localStorage.setItem(STORAGE_KEY_LAST_SUBMISSION, Date.now().toString());
    await fetchFeedback();
    return newFeedback;
  };

  const updateFeedback = async (
    feedbackId: string,
    payload: Partial<SubmitFeedbackPayload>,
  ) => {
    await feedbackService.updateFeedback(feedbackId, payload);
    await fetchFeedback();
  };

  const openFeedbackModal = (
    type: "feedback" | "issue" | "suggestion" = "feedback",
  ) => {
    setModalInitialType(type);
    setIsModalOpen(true);
  };

  const closeFeedbackModal = () => {
    setIsModalOpen(false);
  };

  const getDismissals = (): FeedbackDismissal[] => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY_DISMISSED);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  };

  const isDismissed = (milestoneId?: string, projectId?: string): boolean => {
    const dismissals = getDismissals();
    const now = Date.now();
    return dismissals.some(
      (d) =>
        now - d.timestamp < DISMISSAL_DURATION &&
        ((!milestoneId && !projectId) ||
          (milestoneId && d.milestoneId === milestoneId) ||
          (projectId && d.projectId === projectId)),
    );
  };

  const dismissPrompt = (milestoneId?: string, projectId?: string) => {
    const dismissals = getDismissals().filter(
      (d) => Date.now() - d.timestamp < DISMISSAL_DURATION,
    );
    dismissals.push({ milestoneId, projectId, timestamp: Date.now() });
    localStorage.setItem(STORAGE_KEY_DISMISSED, JSON.stringify(dismissals));
  };

  const hasRecentSubmission = (): boolean => {
    try {
      const lastSubmission = localStorage.getItem(STORAGE_KEY_LAST_SUBMISSION);
      if (!lastSubmission) return false;
      return Date.now() - parseInt(lastSubmission, 10) < SUBMISSION_COOLDOWN;
    } catch {
      return false;
    }
  };

  return (
    <FeedbackContext.Provider
      value={{
        feedback,
        isLoading,
        error,
        isModalOpen,
        modalInitialType,
        fetchFeedback,
        submitFeedback,
        updateFeedback,
        openFeedbackModal,
        closeFeedbackModal,
        isDismissed,
        dismissPrompt,
        hasRecentSubmission,
      }}
    >
      {children}
    </FeedbackContext.Provider>
  );
}

export function useFeedback() {
  const context = useContext(FeedbackContext);
  if (!context) {
    throw new Error("useFeedback must be used within a FeedbackProvider");
  }
  return context;
}
